import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { m as motion } from "framer-motion";
import ThemeContext from "@/components/theme-context";
import { useIsXs } from "@/hooks/useMediaQuery";
import HeaderLink from "@/layouts/partials/header-link";
import HeaderNavItem from "./header-nav-item";

const Header: React.FC = () => {
  const isXs = useIsXs();
  const { setHeaderHeight, navigationHidden } = useContext(ThemeContext);
  const headerRef = useRef<HTMLElement>(null);

  const calculateHeaderDimensions = useCallback(() => {
    if (headerRef?.current) setHeaderHeight(headerRef.current.clientHeight);
  }, [headerRef, setHeaderHeight]);

  useEffect(() => {
    window.addEventListener("resize", calculateHeaderDimensions);

    calculateHeaderDimensions();

    return () => {
      window.removeEventListener("resize", calculateHeaderDimensions);
    };
  }, [calculateHeaderDimensions]);

  // Show the centered "Julia Sellmann" logo like on large screens, down to
  // 375px (the smallest width where it still fits between Work and Info).
  // Below that it is hidden. On project pages the navigation state decides.
  const logoHidden = useMemo<boolean>(() => {
    if (navigationHidden === null) return !isXs;
    return !navigationHidden;
  }, [isXs, navigationHidden]);

  const navItemsHidden = useMemo<boolean>(() => {
    if (navigationHidden === null) return false;
    return navigationHidden;
  }, [navigationHidden]);

  return (
    <header className="group">
      <div className="relative z-40 text-front">
        <nav
          className="fixed inset-x-0 top-0 text-base tracking-tight uppercase sm:text-lg md:text-xl xl:text-2xl"
          ref={headerRef}
        >
          <ul className="min-h-[4rem] flex items-stretch justify-between">
            <motion.li
              variants={{
                show: {
                  display: "block",
                  opacity: 1,
                  y: 0,
                  transitionEnd: {
                    display: "block",
                  },
                },
                hide: {
                  y: -10,
                  opacity: 0,
                  transitionEnd: {
                    display: "none",
                  },
                },
              }}
              initial="show"
              transition={{ ease: "easeOut" }}
              animate={!logoHidden ? "show" : "hide"}
              className="absolute inset-0 flex items-center justify-center text-center md:inset-x-1/4"
            >
              <HeaderLink to="/">
                <span className="relative py-0.5">Julia Sellmann</span>
                <span className="sr-only">| Home</span>
              </HeaderLink>
            </motion.li>
            <HeaderNavItem to="/work" hidden={navItemsHidden}>
              Archive
            </HeaderNavItem>
            <HeaderNavItem to="/info" hidden={navItemsHidden}>
              Info
            </HeaderNavItem>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
